import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import founderImage from "@/assets/nikita-founder-about.jpg";

const About = () => {
  return (
    <>
      <Helmet>
        <title>About Nikita Singhal - Functional Nutritionist in Gurgaon | Renew Santulan</title>
        <meta name="description" content="Meet Nikita Singhal, a certified functional nutritionist in Gurgaon. Learn her journey from health-conscious mom to expert in PCOD, diabetes, and gut health management." />
        <link rel="canonical" href="https://renewsantulan.com/about" />
        <meta property="og:url" content="https://renewsantulan.com/about" />
        <meta property="og:title" content="About Nikita Singhal - Functional Nutritionist in Gurgaon" />
        <meta property="og:description" content="Meet Nikita Singhal, a certified functional nutritionist in Gurgaon specializing in PCOD, diabetes, and gut health." />
        <meta name="twitter:url" content="https://renewsantulan.com/about" />
        <meta name="twitter:title" content="About Nikita Singhal - Functional Nutritionist in Gurgaon" />
        <meta name="twitter:description" content="Meet Nikita Singhal, a certified functional nutritionist in Gurgaon specializing in PCOD, diabetes, and gut health." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Navigation />
      
      <main className="section-padding">
        <div className="container-custom max-w-5xl mx-auto">
          <article className="space-y-8 sm:space-y-12">
            {/* Hero Section */}
            <header className="text-center space-y-4">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground">
                About Nikita Singhal - Functional Nutritionist in Gurgaon
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground">
                Journey from a Health-Conscious Mom to Expert Functional Nutritionist
              </p>
            </header>

            {/* Image Section */}
            <figure className="flex justify-center px-4">
              <div className="relative w-full max-w-2xl aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={founderImage} 
                  alt="Nikita Singhal - Certified Functional Nutritionist in Gurgaon specializing in PCOD, diabetes, and gut health" 
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </figure>

            {/* Story Section */}
            <section className="prose prose-lg max-w-none space-y-6 text-muted-foreground px-4 sm:px-6 md:px-8">
              <p className="text-base sm:text-lg leading-relaxed">
                Hi, I'm a <span className="font-semibold text-foreground">Functional Nutritionist</span>—but long before I earned that title, I was just a curious, health-conscious mom trying to do her best with whatever knowledge she could find.
              </p>

              <p className="text-base sm:text-lg leading-relaxed">
                When I got married into a joint family, my obsession with balanced meals—packed with fresh veggies, raw salads, and whole foods—was met with a bit of resistance. But I held my ground, because I believed deeply that what we eat shapes how we live.
              </p>

              <p className="text-base sm:text-lg leading-relaxed">
                Motherhood only made this belief stronger. From the moment my kids started solids, I was intentional about what went on their plates. I've always believed: <span className="font-semibold text-foreground italic">"You are what you eat"</span>—and the habits we build in childhood shape the health we carry into adulthood.
              </p>

              <div className="border-l-4 border-primary pl-6 my-8">
                <p className="text-base sm:text-lg leading-relaxed">
                  Years later, when my daughter was diagnosed with PCOD, I found myself standing at a crossroads. I could follow the conventional route—hormone therapy, medication—or I could dig deeper. We chose the latter. We made small changes: cutting back on sugar, reducing dairy, making food our medicine. It wasn't always easy, but it worked. And it opened my eyes to the power of food and lifestyle in healing.
                </p>
              </div>

              <p className="text-base sm:text-lg leading-relaxed">
                Then, in 2015, life brought another wake-up call—my mother was diagnosed with blood cancer. That moment broke me, but it also built a fire within me. While we followed medical treatment, I began researching relentlessly: What foods help? What hinders healing? Can nourishment ease suffering?
              </p>

              <p className="text-base sm:text-lg leading-relaxed font-semibold text-foreground">
                The answer, I discovered, was a resounding yes.
              </p>

              {/* Key Beliefs Section */}
              <aside className="bg-primary/5 rounded-xl p-6 sm:p-8 my-8 border border-primary/10">
                <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-4">
                  My Core Beliefs as a Functional Nutritionist:
                </h2>
                <ul className="space-y-3 text-base sm:text-lg">
                  <li className="flex items-start gap-3">
                    <span className="text-primary text-2xl leading-none">•</span>
                    <span>Food is not just fuel—it's <span className="font-semibold text-foreground">information</span>. It's <span className="font-semibold text-foreground">medicine</span>. It's <span className="font-semibold text-foreground">transformation</span>.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary text-2xl leading-none">•</span>
                    <span>Healing doesn't need to start with pills. It <span className="font-semibold text-foreground">starts on your plate</span>.</span>
                  </li>
                </ul>
              </aside>

              <p className="text-base sm:text-lg leading-relaxed">
                I created my programs for people like you—who are tired of feeling tired, bloated, stressed, hormonal, or simply "not yourself." You may not know where to begin, and that's okay. That's why I'm here.
              </p>

              <p className="text-base sm:text-lg leading-relaxed">
                Whether you're dealing with <strong>PCOD/PCOS</strong>, <strong>gut issues</strong>, <strong>diabetes</strong>, <strong>thyroid disorders</strong>, weight management challenges, autoimmune conditions, or just want to feel good in your skin again—I can guide you, step by step. With personalized functional nutrition. With mindset. With heart.
              </p>

              <div className="text-center bg-accent/10 rounded-xl p-6 sm:p-8 my-8">
                <p className="text-lg sm:text-xl font-semibold text-foreground mb-2">
                  If my story resonates with you, maybe it's time to start your own healing journey.
                </p>
                <p className="text-base sm:text-lg text-muted-foreground">
                  I'd be honoured to be a part of your journey toward a healthier, happier YOU through evidence-based functional nutrition in Gurgaon.
                </p>
                <p className="text-xl sm:text-2xl font-bold text-primary mt-4">
                  Let's begin your transformation today.
                </p>
              </div>
            </section>
          </article>
        </div>
      </main>

        <Footer />
      </div>
    </>
  );
};

export default About;
